import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Users, RotateCcw, X, Sparkles, MessageCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { AuthContext, WS_URL } from "../App";

// Truth or Dare questions/dares
const TRUTHS = [
  "What's your most embarrassing moment?",
  "What's the biggest lie you've ever told?",
  "What's your secret talent?",
  "What's the weirdest dream you've had?",
  "What's your guilty pleasure?",
  "What's the most childish thing you still do?",
  "What's something you've never told anyone?",
  "What's your biggest fear?",
  "What's the most trouble you've been in?",
  "If you could be invisible for a day, what would you do?",
  "What's the most embarrassing song on your playlist?",
  "What's the worst date you've been on?",
  "What's your most unpopular opinion?",
  "What's the pettiest thing you've done?",
  "What would you do with a million dollars?"
];

const DARES = [
  "Do your best dance move",
  "Sing the chorus of your favorite song",
  "Talk in an accent for the next 2 minutes",
  "Do 10 jumping jacks",
  "Make a funny face and keep it for 30 seconds",
  "Tell a joke",
  "Do your best impression of another player",
  "Speak in rhymes for 1 minute",
  "Act like your favorite animal",
  "Give a compliment to everyone in the room",
  "Do your best evil laugh",
  "Pretend to be a news reporter",
  "Do a dramatic reading of a text message",
  "Mime something and let others guess",
  "Talk in slow motion for 1 minute"
];

const ADULT_TRUTHS = [
  "What's your biggest turn-on?",
  "Have you ever had a crush on a friend's partner?",
  "What's the craziest thing on your bucket list?",
  "What's your most scandalous story?",
  "What's your guilty pleasure that you hide?",
  "Have you ever been caught doing something naughty?",
  "What's the wildest thing you've done for love?",
  "What's your secret fantasy?",
  "What's the most rebellious thing you've done?",
  "If you could date any celebrity, who would it be?"
];

const ADULT_DARES = [
  "Send a flirty message to someone",
  "Do your sexiest dance move",
  "Give someone a compliment about their looks",
  "Describe your ideal date in detail",
  "Act out your best pick-up line",
  "Do a seductive pose",
  "Tell us about your most awkward romantic moment",
  "Rate yourself on a scale of 1-10 and explain why",
  "Describe your celebrity crush in detail",
  "Do an impression of someone flirting"
];

const TruthOrDareGame = ({ onClose, isAdult = false }) => {
  const { token, user } = useContext(AuthContext);
  const [view, setView] = useState('lobby');
  const [currentTable, setCurrentTable] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [challengeType, setChallengeType] = useState(null); // 'truth' or 'dare'
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinAngle, setSpinAngle] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const wsRef = useRef(null);

  const truths = isAdult ? [...TRUTHS, ...ADULT_TRUTHS] : TRUTHS;
  const dares = isAdult ? [...DARES, ...ADULT_DARES] : DARES;

  // Spin the wheel
  const spinWheel = () => {
    if (players.length < 2) {
      toast.error("Need at least 2 players");
      return;
    }
    
    setIsSpinning(true);
    const spins = 5 + Math.random() * 5; // 5-10 full rotations
    const targetIndex = Math.floor(Math.random() * players.length);
    const targetAngle = spins * 360 + (targetIndex * (360 / players.length));
    
    setSpinAngle(targetAngle);
    
    setTimeout(() => {
      setIsSpinning(false);
      setCurrentPlayer(players[targetIndex]);
      setChallengeType(null);
      setCurrentChallenge(null);
    }, 3000);
  };

  // Choose truth or dare
  const chooseType = (type) => {
    setChallengeType(type);
    const challenges = type === 'truth' ? truths : dares;
    const randomChallenge = challenges[Math.floor(Math.random() * challenges.length)];
    setCurrentChallenge(randomChallenge);
    
    // Broadcast to other players
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'challenge',
        player: currentPlayer,
        challenge_type: type,
        challenge: randomChallenge
      }));
    }
  };

  // Next round
  const nextRound = () => {
    setCurrentPlayer(null);
    setChallengeType(null);
    setCurrentChallenge(null);
    spinWheel();
  };

  // WebSocket
  const connectWs = useCallback((tableCode, hosting) => {
    if (wsRef.current) wsRef.current.close();
    
    const ws = new WebSocket(`${WS_URL}/ws/truthdare/${tableCode}?token=${token}`);
    
    ws.onopen = () => {
      console.log("Truth or Dare WS connected");
      setIsHost(hosting);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'game_state':
          setPlayers(data.players || []);
          setGameStarted(data.game_started);
          if (data.current_player) setCurrentPlayer(data.current_player);
          if (data.challenge) {
            setChallengeType(data.challenge_type);
            setCurrentChallenge(data.challenge);
          }
          break;
        case 'player_joined':
          setPlayers(prev => [...prev.filter(p => p.user_id !== data.player.user_id), data.player]);
          toast.info(`${data.player.username} joined!`);
          break;
        case 'player_left':
          setPlayers(prev => prev.filter(p => p.user_id !== data.user_id));
          break;
        case 'spin':
          setCurrentPlayer(data.player);
          setChallengeType(null);
          setCurrentChallenge(null);
          break;
        case 'challenge':
          setCurrentPlayer(data.player);
          setChallengeType(data.challenge_type);
          setCurrentChallenge(data.challenge);
          break;
      }
    };
    
    wsRef.current = ws;
  }, [token]);

  const createGame = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCurrentTable({ code });
    connectWs(code, true);
    setView('game');
    setPlayers([{ user_id: user?.user_id, username: user?.username }]);
  };

  const joinGame = (code) => {
    setCurrentTable({ code });
    connectWs(code, false);
    setView('game');
  };

  const leaveGame = () => {
    if (wsRef.current) wsRef.current.close();
    setView('lobby');
    setCurrentTable(null);
    setPlayers([]);
    setGameStarted(false);
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  if (view === 'lobby') {
    return (
      <div className={`w-full h-full bg-gradient-to-b ${isAdult ? 'from-pink-900 to-red-950' : 'from-cyan-900 to-blue-950'} p-4`}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              {isAdult ? '🔥' : '🎭'} TRUTH OR DARE {isAdult && <span className="text-xs bg-red-600 px-2 py-0.5 rounded">18+</span>}
            </h2>
            <button onClick={onClose} className="text-white hover:text-red-400">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="bg-black/20 rounded-lg p-6 mb-4 text-center">
            <p className="text-gray-300 mb-4">Spin the wheel and face your fate!</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={createGame}
                className={`bg-gradient-to-r ${isAdult ? 'from-pink-600 to-red-600' : 'from-cyan-600 to-blue-600'} text-white py-4 rounded-lg font-bold hover:opacity-90`}
              >
                🎮 CREATE GAME
              </button>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  placeholder="Enter code..."
                  className="px-4 py-2 rounded bg-black/30 text-white border border-white/30 uppercase text-center"
                  id="tod-code-input"
                />
                <button
                  onClick={() => {
                    const code = document.getElementById('tod-code-input').value;
                    if (code) joinGame(code.toUpperCase());
                  }}
                  className="bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-500"
                >
                  JOIN
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full bg-gradient-to-b ${isAdult ? 'from-pink-900 to-red-950' : 'from-cyan-900 to-blue-950'} flex flex-col`}>
      {/* Header */}
      <div className="bg-black/40 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white font-bold">🎭 Game: {currentTable?.code}</span>
          <span className="text-gray-400 flex items-center gap-1">
            <Users className="w-4 h-4" /> {players.length}
          </span>
        </div>
        <button onClick={leaveGame} className="text-red-400 hover:text-red-300 text-sm">
          Leave
        </button>
      </div>

      {/* Game Area */}
      <div className="flex-1 flex items-center justify-center p-4 gap-8">
        {/* Wheel */}
        <div className="relative">
          <div 
            className="w-64 h-64 rounded-full border-8 border-yellow-500 relative overflow-hidden transition-transform duration-[3000ms] ease-out"
            style={{ transform: `rotate(${spinAngle}deg)` }}
          >
            {players.map((player, index) => {
              const angle = (index * 360) / players.length;
              const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-teal-500'];
              return (
                <div
                  key={player.user_id}
                  className={`absolute w-full h-full ${colors[index % colors.length]}`}
                  style={{
                    clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.tan(Math.PI / players.length)}% 0%)`,
                    transform: `rotate(${angle}deg)`,
                    transformOrigin: '50% 50%'
                  }}
                >
                  <div 
                    className="absolute text-white font-bold text-xs"
                    style={{
                      top: '20%',
                      left: '50%',
                      transform: 'translateX(-50%) rotate(90deg)'
                    }}
                  >
                    {player.username?.slice(0, 8)}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Arrow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-0 h-0 border-l-8 border-r-8 border-t-12 border-l-transparent border-r-transparent border-t-yellow-400 z-10" />
        </div>

        {/* Challenge Panel */}
        <div className="w-80 bg-black/30 rounded-lg p-4">
          {!currentPlayer ? (
            <div className="text-center">
              <p className="text-gray-400 mb-4">Spin the wheel to pick a player!</p>
              <button
                onClick={spinWheel}
                disabled={isSpinning || players.length < 2}
                className={`${isAdult ? 'bg-pink-600 hover:bg-pink-500' : 'bg-cyan-600 hover:bg-cyan-500'} text-white px-8 py-3 rounded-lg font-bold disabled:opacity-50 flex items-center gap-2 mx-auto`}
              >
                <Sparkles className="w-5 h-5" /> SPIN!
              </button>
              {players.length < 2 && (
                <p className="text-yellow-400 text-sm mt-2">Need at least 2 players</p>
              )}
            </div>
          ) : !challengeType ? (
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-4">
                {currentPlayer.username}'s turn!
              </div>
              <p className="text-gray-400 mb-4">Choose your fate:</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => chooseType('truth')}
                  className="bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-lg font-bold flex flex-col items-center gap-1"
                >
                  <MessageCircle className="w-6 h-6" />
                  TRUTH
                </button>
                <button
                  onClick={() => chooseType('dare')}
                  className="bg-red-600 hover:bg-red-500 text-white py-4 rounded-lg font-bold flex flex-col items-center gap-1"
                >
                  <Zap className="w-6 h-6" />
                  DARE
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className={`text-xl font-bold ${challengeType === 'truth' ? 'text-blue-400' : 'text-red-400'} mb-2`}>
                {challengeType.toUpperCase()}
              </div>
              <div className="text-2xl font-bold text-white mb-4">
                {currentPlayer.username}
              </div>
              <div className="bg-white/10 rounded-lg p-4 mb-4">
                <p className="text-white text-lg">{currentChallenge}</p>
              </div>
              <button
                onClick={nextRound}
                className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 mx-auto"
              >
                <RotateCcw className="w-4 h-4" /> NEXT ROUND
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Players List */}
      <div className="bg-black/40 px-4 py-2">
        <div className="flex items-center gap-4 justify-center">
          {players.map(player => (
            <span 
              key={player.user_id}
              className={`px-3 py-1 rounded ${
                currentPlayer?.user_id === player.user_id 
                  ? 'bg-yellow-500 text-black' 
                  : 'bg-white/20 text-white'
              } font-medium text-sm`}
            >
              {player.username}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TruthOrDareGame;
